import { Injectable } from '@angular/core';
import { HttpClientService } from '../../../http-client.service';
import { environment } from '../../../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { IOrder, IOrdersFromAPI, IShipTo, IShipToFromAPI } from '../shared/interfaces/orders.interface';
import * as _ from 'lodash';
import { ApplicationInsightsService } from '../../../shared/application-insights.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ISelectedProductListDetails, IProductDetails } from '../../../shared/shared/interfaces/shared.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpParams } from '@angular/common/http';

/**
 * Represents the state of a wide-range search that may require polling.
 *
 * - 'polling': backend returned 202, data still being populated. Component
 *   should show a "loading extended history" message. Emits repeatedly with
 *   updated elapsedMs until data arrives or timeout.
 * - 'data': data ready. Component should render orders normally.
 * - 'timeout': polling exceeded max allowed time. Component should show error.
 */
export type SearchState =
  | { kind: 'polling'; elapsedMs: number; message: string }
  | { kind: 'data'; data: any; cachedAt?: string }
  | { kind: 'timeout'; elapsedMs: number };

@Injectable()
export class OrdersService {
  // Add this property to your class
  canReload: boolean;
  moveToStep = 0;
  backArrowUrl: string;
  sourceValueOfOrdersView$: BehaviorSubject<{search: {searchKey: 'territory' | 'customer',
      searchValue: {shipToNo: string, salesOrg: { eclEBS: string[], nlcSAP: string[] }} | {territoryId: string, systemId: string}}}> =
    new BehaviorSubject<{search: {searchKey: 'territory' | 'customer',
        searchValue: {shipToNo: string, salesOrg: { eclEBS: string[], nlcSAP: string[] }} | {territoryId: string, systemId: string}}}>({
      search: {searchKey: 'territory', searchValue: {territoryId: '', systemId: ''}}
  });
  orderDetails: any;
  orderStatusList: any[] = [];//contains result from Accounts/Shipto API
  filteredOrderStatusList: any[] = [];//contains filtered Accounts/Shipto
  filteredOrders: any[] = [];//contains filtered OrdersList
  selectedCustomer: any;
  // TODO: 11052019-Can be merged with selectedCustomer property.
  selectedCustomerScrollOffset: number = 0;
  selectedOrder: { details: ISelectedProductListDetails, products: IProductDetails[], scrollOffset: number } = {} as { details: any, products: any[], scrollOffset: number };
  filterOption: { customerName: string[], shiptoNumber: string[], soldtoNumber: string[], orderNumber: string[], productNumber: string[], productName: string[] } =
    {} as { customerName: string[], shiptoNumber: string[], soldtoNumber: string[], orderNumber: string[], productNumber: string[], productName: string[] };
  filter: { filterType: 'customerName' | 'shiptoNumber' | 'soldtoNumber' | 'orderNumber' | 'productNumber' | 'productName', filterValue: string };
  //Below is specific for Order-List filteration
  filterValuesForOrderList: { filterType: 'productNumber' | 'productName', filterValue: string | null };

  private readonly restApiURL: string;
  private readonly restApiURL_new: string;

  constructor(private httpClientService: HttpClientService, private applicationInsightsService: ApplicationInsightsService, private snackBar: MatSnackBar) {
    this.restApiURL = environment.apiHost;
    this.restApiURL_new = environment.apiHost_new;
  }

  /*
  Below method is envoked when a Territory user logs in
  */ 
  public getOrdersByTerritory(territoryId: string, systemId: string): Observable<any> {
    return this.httpClientService.get<any>(`${this.restApiURL}/orders/fetchShipTos?territoryId=${territoryId}&systemId=${systemId}`).pipe(
      map((res: any) => {
        this.orderStatusList = res.orders.orders;
        this.getFilterOptions();//this is done to populate the filter dropdown
        this.resetSelections();//to remove any stored value in dropdown
      }), catchError((error: HttpErrorResponse) => {
        this.applicationInsightsService.trackExceptions(error);
        return throwError(error);
      })
    );
  }

  /*
  Below method is envoked from customer-list when a Non-Territory user logs in
  */
  public getOrdersByCustomer(customerNumber: string, salesOrg: { eclEBS: string[], nlcSAP: string[] }): Observable<any> {
    const strSalesOrg = JSON.stringify(salesOrg);
    return this.httpClientService.get<any>(`${this.restApiURL}/orders/fetchShipTos?customerNumber=${customerNumber}&salesOrg=${strSalesOrg}`).pipe(
      map((res: any) => {
        this.orderStatusList = res.orders;
        this.getFilterOptions(); //Invoking this method to populate the filters
        this.resetSelections();//to remove any stored value in dropdown
      }), catchError((error: HttpErrorResponse) => {
        this.applicationInsightsService.trackExceptions(error);
        return throwError(error);
      })
    );
  }

  /*Below method is envoked from Product-list C to fetch the lineItems for a 
  specific order*/
  public getProductList(): Observable<any> {
    let sample = (this.selectedOrder.details.orderLineNumRef);
    let ordersArray = <any[]>([]);
    sample.forEach(doc => {
      let orderQueryFilter = {} as ISelectedProductListDetails;
      orderQueryFilter.orderLineNumRef = doc.orderLineNumRef;
      orderQueryFilter.orderLineStage = doc.orderLineStage;
      ordersArray.push(orderQueryFilter);
    });
    sample = JSON.stringify(ordersArray);
    return this.httpClientService.get<IOrdersFromAPI>(`${this.restApiURL}/orders/${this.selectedOrder.details.orderID}/fetchLineItems?shipTo=${this.selectedCustomer.shipToNumber}&systemId=${this.selectedOrder.details.systemID}&salesOrg=${this.selectedOrder.details.salesOrg}&deliveryID=${this.selectedOrder.details.deliveryID}&orderLineRef=${sample}`).pipe(
      map((response: any) => {
        this.selectedOrder.products = response;
      }), catchError((error: HttpErrorResponse) => {
        this.applicationInsightsService.trackExceptions(error);
        return throwError(error);
      }),
    );
  }

  // private groupOrdersByCustomer(shipTosResponse: IShipToFromAPI): any[] {
  //   let shipTosList=[];
  //   _.each(shipTosResponse.shipTos,(shipTo:IShipTo)=>{
  //     let isOrderGrouped=false;
  //     _.each(shipTosList, (item: any) => {
  //             if (shipTo.shipToNumber === item.shipToNumber && shipTo.orderSourceSystem === item.orderSourceSystem) {
  //               isOrderGrouped = true;
  //               // TODO:08302019 This hardcoded weight units needs to be removed.
  //               const currentOrderDetails = {
  //                 orderNumber: order.orderNumber, customerPONumber: order.customerPONumber, orderCreationDate: order.orderCreationDate,
  //                 requestedDeliveryDate: order.requestedDeliveryDate, orderWeight: order.orderWeight, orderLineWeightUnits: 'KG',
  //                 orderLineItemCount: order.orderLineItemCount,
  //                 deliveryDetails: [...order.deliveries], productDetails: [...order.Products]
  //               };



  //   })


  // }

  // private groupOrdersByCustomer(ordersResponse: IShipTo[]): any[] {
  //   let orderStatusListByCustomer = [];
  //   _.each(ordersResponse.orders, (order: IOrder) => {
  //     let isOrderGrouped = false;
  //     _.each(orderStatusListByCustomer, (item: any) => {
  //       if (order.shipToNumber === item.shipToNumber && order.orderSourceSystem === item.orderSourceSystem) {
  //         isOrderGrouped = true;
  //         // TODO:08302019 This hardcoded weight units needs to be removed.
  //         const currentOrderDetails = {
  //           orderNumber: order.orderNumber, customerPONumber: order.customerPONumber, orderCreationDate: order.orderCreationDate,
  //           requestedDeliveryDate: order.requestedDeliveryDate, orderWeight: order.orderWeight, orderLineWeightUnits: 'KG',
  //           orderLineItemCount: order.orderLineItemCount,
  //           deliveryDetails: [...order.deliveries], productDetails: [...order.Products]
  //         };
  //         item.orders.push(currentOrderDetails);
  //       }
  //     });

  //     if (!isOrderGrouped) {
  //       // TODO:08302019 This hardcoded weight units needs to be removed.
  //       const shipToItem = {
  //         orderSourceSystem: order.orderSourceSystem,
  //         shipToName: order.shipToName,
  //         shipToAddress: {...order.shipToAddress},
  //         shipToNumber: order.shipToNumber,
  //         soldToNumber: order.soldToNumber,
  //         shipToSalesOrg: order.shipToSalesOrg,
  //         orders: [
  //           {
  //             orderNumber: order.orderNumber, customerPONumber: order.customerPONumber, orderCreationDate: order.orderCreationDate,
  //             requestedDeliveryDate: order.requestedDeliveryDate, orderWeight: order.orderWeight, orderLineWeightUnits: 'KG',
  //             orderLineItemCount: order.orderLineItemCount,
  //             deliveryDetails: [...order.deliveries], productDetails: [...order.Products]
  //           }
  //         ]
  //       };
  //       orderStatusListByCustomer.push(shipToItem);
  //     }
  //   });
  //   return orderStatusListByCustomer;
  // }

  /*This method will help in populating the product# & productName Dropdown 
  //in filter of Orders Page*/
  private getProductFilterOptions() {
    if (this.orderDetails.orders.length > 0) {
      if (this.filteredOrderStatusList.length > 0 && this.filter.filterType === "orderNumber") {
        // Non-Territory product filter condition
        this.filterOption.productNumber = _.uniq(_.flatten(_.map(this.orderDetails.orders, (item) => {
          if (this.filter.filterValue === item.orderNumber) {
            return _.flatten(_.map(item.Products, (p) => {
              return (p.sKU);
            }));
          } 
          // Territory product filter condition
          else if (this.filter.filterValue === this.filteredOrderStatusList[0]?.filteredOrders[0]?.orderNum) {
            if (item.orderNumber === this.filter.filterValue) {
              return _.flatten(_.map(item.Products, (p) => {
                return (p.sKU);
              }));
            }
          }
        })));
        this.filterOption.productName = _.uniq(_.flatten(_.map(this.orderDetails.orders, (item) => {
          if (this.filter.filterValue === item.orderNumber) {
            return _.flatten(_.map(item.Products, (p) => {
              return (p.productName);
            }));
          }
        })));
      } else {
        this.filterOption.productNumber = _.uniq(_.flatten(_.map(this.orderDetails.orders, (item) => {
          return _.flatten(_.map(item.Products, (p) => {
            return (p.sKU);
          }));
        })));
        this.filterOption.productName = _.uniq(_.flatten(_.map(this.orderDetails.orders, (item) => {
          return _.flatten(_.map(item.Products, (p) => {
            return (p.productName);
          }));
        })));
      }
      // Reset filters
      // this.resetAppliedFilters();
    }
  }

  //This method will help in populating the customerName, custNumber, orderNum,  
  //SoldTo number in filter DropDown
  private getFilterOptions() {
    if (this.orderStatusList.length > 0) {
      this.filterOption.orderNumber = _.flatten(_.map(this.orderStatusList, (item) => {
        return (_.map(item.orders, 'orderNum'));
      }));
      this.filterOption.shiptoNumber = _.map(this.orderStatusList, 'shipToNumber');
      this.filterOption.soldtoNumber = _.uniq(_.flatten(_.map(this.orderStatusList, (item) => {
        return (_.map(item.orders, 'soldToNumber'));
      })));
      this.filterOption.customerName = _.uniq(_.flatten(_.map(this.orderStatusList, (item) => {
        return (_.map(item.orders, 'shipToName'));
      })));
      this.filterOption.productNumber = _.uniq(_.flatten(_.map(this.orderStatusList, (item) => {
        return _.flatten(_.map(item.orders, (orderItem) => {
          return (_.map(orderItem.productDetails, 'sKU'));
        }));
      })));
      this.filterOption.productName = _.uniq(_.flatten(_.map(this.orderStatusList, (item) => {
        return _.flatten(_.map(item.orders, (orderItem) => {
          return (_.map(orderItem.productDetails, 'productName'));
        }));
      })));
      // Reset filters
      this.resetAppliedFilters();
    }
  }

  //This is called whenever we want to remove the values entered in the filter dropdown
  private resetSelections() {
    this.selectedCustomer = {};
    this.selectedCustomerScrollOffset = 0;
    this.selectedOrder = {} as { details: any, products: any[], scrollOffset: number };
  }

  public resetAppliedFilters(): void {
    this.filter = <any>([]);
    this.filteredOrderStatusList = [];
    for (const item of this.orderStatusList) {
      item.filteredOrders = [];
    }
  }

  //This method helps in filtering Orders in Order-list component based on ProductID and ProdName
  public getFilteredOrderList(filterType: any, filterValue: string, orders: any): void {
    this.filterValuesForOrderList = { filterType: filterType, filterValue: filterValue };//need this in order to avoid extra network call
    switch (this.filterValuesForOrderList.filterType) {
      case 'productNumber':
        orders = orders.filter((x) => {
          if (x.Products.find((d: any) => d.sKU == this.filterValuesForOrderList.filterValue)) {
            return x;
          }
        });
        break;
      case 'productName':
        orders = orders.filter((x) => {
          if (x.Products.find((d: any) => d.productName == this.filterValuesForOrderList.filterValue)) {
            return x;
          }
        });
        break;
    }
    this.filteredOrders = orders;
    return orders;
  }

  /*This is for the POD download functionality*/
  downloadProofOfDelivery(deliveryNumber: string) {
    const url = `${this.restApiURL_new}/pod/${deliveryNumber.trim()}`;
    // Return the observable so the Component can subscribe to it
    return this.httpClientService.get(url, {
      responseType: 'blob',
      observe: 'response'
    } as any);
  }

  /*
  Below method helps in filtering the ShipTo number cards based on what is entered
  in the filters on 1st Page.
  in customer-list component, this.customers is assigned with filteredOrderstatusList
  which contains only the filtered values
  */
  public getFilteredCustomerList(filterType: any, filterValue: string): void {
    this.filter = { filterType: filterType, filterValue: filterValue };
    let filteredList = <any[]>([]);
    switch (filterType) {
      case 'customerName': filteredList = _.filter(this.orderStatusList, (item) => {
        item.filteredOrders = [];
        if (_.filter(item.orders, { shipToName: filterValue }).length > 0) {
          const filteredShipTos = item.orders.filter(order => order.shipToName === filterValue);
          item.filteredOrders = filteredShipTos;
          return item;
        }
      });
        break;
      case 'shiptoNumber': filteredList = _.filter(this.orderStatusList, ['shipToNumber', filterValue]);
        break;
      case 'soldtoNumber': filteredList = _.filter(this.orderStatusList, (item) => {
        item.filteredOrders = [];
        if (_.filter(item.orders, { soldToNumber: filterValue }).length > 0) {
          const filteredOrders = item.orders.filter(order => order.soldToNumber === filterValue);
          item.filteredOrders = filteredOrders;
          return item;
        }
      });
        break;
      case 'orderNumber':
        let orderListduplicate: any[];//Creating this so that OrderCount of the stable orderstatuslist 
        orderListduplicate = _.cloneDeep(this.orderStatusList);
        filteredList = _.filter(orderListduplicate, (item) => {
          item.filteredOrders = [];
          if (_.filter(item.orders, { orderNum: filterValue }).length > 0) {
            const filteredOrders = item.orders.filter(order => order.orderNum === filterValue);
            item.filteredOrders = filteredOrders;
            item.orderCount = filteredOrders.length;
            return item;
          }
        });
        break;
      case 'productNumber':
        for (let i = 0; i < this.orderStatusList.length; i++) {
          this.orderStatusList[i].filteredOrders = [];
          this.orderStatusList[i].orders.forEach((order: any) => {
            if (order && order.productDetails && order.productDetails.length > 0) {
              order.productDetails.forEach((product: any) => {
                if (product.sKU === filterValue) {
                  this.orderStatusList[i].filteredOrders.push(order);
                }
              });
            }
          });
        }
        filteredList = this.orderStatusList.map(orderStatusList => {
          orderStatusList.filteredOrders = _.uniqBy(orderStatusList.filteredOrders, 'orderNumber');
          return orderStatusList;
        }).filter(({ filteredOrders }) => filteredOrders.length > 0);
        break;
      case 'productName':
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < this.orderStatusList.length; i++) {
          this.orderStatusList[i].filteredOrders = [];
          this.orderStatusList[i].orders.forEach((order: any) => {
            if (order && order.productDetails && order.productDetails.length > 0) {
              order.productDetails.forEach((product: any) => {
                if (product.productName === filterValue) {
                  this.orderStatusList[i].filteredOrders.push(order);
                }
              });
            }
          });
        }
        filteredList = this.orderStatusList.map(orderStatusList => {
          orderStatusList.filteredOrders = _.uniqBy(orderStatusList.filteredOrders, 'orderNumber');
          return orderStatusList;
        }).filter(({ filteredOrders }) => filteredOrders.length > 0);
        break;
    }
    this.filteredOrderStatusList = filteredList;
  }

  //This method helps in fetching the orders for a specific Shipto from Middleware
  //It gets invoked when users clicks on a ShipTo to see the orderdetails
  public FetchOrdersForShipTo(shipToNumber: string, salesOrg: string): Observable<any> {
    return this.httpClientService.get<IOrdersFromAPI>(`${this.restApiURL}/orders/getOrdersList?customerNumber=${shipToNumber}&salesOrg=${salesOrg}`).pipe(
      map((response: any) => {
        this.orderDetails = response;
        this.getProductFilterOptions();
      }), catchError((error: HttpErrorResponse) => {
        this.applicationInsightsService.trackExceptions(error);
        return throwError(error);
      }),
    );
  }

  /*public FetchOrdersForNonTerrotory(searchNumber:string, fromDate:string,toDate:string):Observable<any>{
    return this.httpClientService.get<IOrdersFromAPI>(`${this.restApiURL_new}/orders/by-shipto/${searchNumber}/${fromDate}/${toDate}`)
    .pipe(
      map((response: any) => {
        this.orderDetails =  response;

      }), catchError((error: HttpErrorResponse) => {
        this.applicationInsightsService.trackExceptions(error);
        return throwError(error);
      }),
    );
  }*/

  /**
   * Fetch orders for non-territory user.
   *
   * Handles two response types from the backend:
   *   - HTTP 200 with order data: emits { kind: 'data', data } once and completes
   *   - HTTP 202 with { status: 'still-loading' }: emits { kind: 'polling', ... }
   *     and re-fires the same request every POLL_INTERVAL_MS until either the
   *     backend returns 200 with data or MAX_ELAPSED_MS is exceeded.
   *
   * The 202 flow happens when the user's requested date range exceeds 90 days
   * and the 2-year cache is being populated in the background. The API tells
   * us to poll; we do so transparently and update the component with progress.
   *
   * Component subscribes and handles each state:
   *   - 'polling': show "loading extended history" UI with elapsed timer
   *   - 'data': render orders normally
   *   - 'timeout': show "took too long" message
   */
  public FetchOrdersForNonTerrotory(
    searchNumber: string,
    fromDate?: string,
    toDate?: string,
  ): Observable<SearchState> {
    const POLL_INTERVAL_MS = 20000;   // Poll every 20 seconds
    const MAX_ELAPSED_MS = 300000;    // Give up after 5 minutes

    let params = new HttpParams();
    // Attach dates only if they exist
    if (fromDate && toDate) {
      params = params.set('fromDate', fromDate).set('toDate', toDate);
    }

    const url = `${this.restApiURL_new}/orders/by-shipto/${searchNumber}`;
    const startTime = Date.now();

    return new Observable<SearchState>((observer) => {
      let timeoutHandle: any = null;
      let cancelled = false;

      const attempt = () => {
        if (cancelled) return;

        const elapsedMs = Date.now() - startTime;

        // Check timeout
        if (elapsedMs > MAX_ELAPSED_MS) {
          observer.next({ kind: 'timeout', elapsedMs });
          observer.complete();
          return;
        }

        this.httpClientService.get(url, {
          params: params,
          observe: 'response',
        } as any).subscribe({
          next: (response: any) => {
            if (cancelled) return;

            const body = response?.body ?? response;
            const status = response?.status;

            // Check for still-loading via status code OR body flag
            const isStillLoading =
              status === 202 || body?.status === 'still-loading';

            if (isStillLoading) {
              observer.next({
                kind: 'polling',
                elapsedMs,
                message:
                  body?.message ??
                  'Data is being prepared for a wider date range. Please wait...',
              });

              // Schedule next attempt
              timeoutHandle = setTimeout(attempt, POLL_INTERVAL_MS);
              return;
            }

            // 200 — data ready
            this.orderDetails = body;
            observer.next({ 
              kind: 'data', 
              data: body,
              cachedAt: body?.cachedAt,
            });
            observer.complete();
          },
          error: (error: HttpErrorResponse) => {
            if (cancelled) return;
            this.applicationInsightsService.trackExceptions(error);
            observer.error(error);
          },
        });
      };

      // First attempt
      attempt();

      // Cleanup on unsubscribe
      return () => {
        cancelled = true;
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
        }
      };
    });
  }
  /*
  Invoked when a Territory user selects a territory in the territory view.
  Fetches ship-tos under the territory (SalesOrgShipTo table joined with
  MuleSoft API 2 account-detail enrichment). No orders are fetched here —
  orders load on drill-in via /by-shipto.
  */
  public getShipTosByTerritory(territoryId: string, systemId: string): Observable<any> {
    return this.httpClientService.get<any>(
      `${this.restApiURL_new}/territory-order/ship-tos?territoryId=${territoryId}&systemId=${systemId}`,
    ).pipe(
      map((res: any) => {
        this.orderStatusList = res.shipTos;
        this.getFilterOptions();
        this.resetSelections();
      }),
      catchError((error: HttpErrorResponse) => {
        this.applicationInsightsService.trackExceptions(error);
        return throwError(error);
      }),
    );
  }
}